<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title text-uppercase">
          <h4>{{ $t('journal_voucher') }}</h4>
        </div>
      </div>
      <ul class="table-top-head">
        <li>
          <div class="page-btn">
            <router-link :to="{name:'JournalVoucher'}" class="btn btn-added">
              <span>Back</span>
            </router-link>
          </div>
        </li>
        <li>
          <a data-bs-toggle="tooltip" data-bs-placement="top" title="Collapse" id="collapse-header"><i class="ti ti-chevron-up"></i></a>
        </li>
      </ul>
    </div>

    <div class="card table-list-card p-3 bg-white fs-7">
    <form @submit.prevent="submitBatch" enctype="multipart/form-data">
      <!-- Header Form Section -->
      <div class="row gx-4 mb-3 voucher-header">
        <div class="col-md-6">
          <div class="d-flex align-items-center">
            <label class="header-label form-label small mb-0">{{$t("voucher_no")}}</label>
            <input type="text" class="form-control header-field" v-model="batches.voucher_no" readonly/>
          </div>
          <div class="header-error"></div>
        </div>

        <div class="col-md-6">
          <div class="d-flex align-items-center">
            <label class="header-label form-label small mb-0">{{$t("voucher_date")}}</label>
            <input type="date" class="form-control header-field" v-model="batches.voucher_date" @change="fetchReceivingNo" />
          </div>
          <div class="header-error">{{ errors.voucher_date }}</div>
        </div>

        <div class="col-md-6">
          <div class="d-flex align-items-center">
            <label class="header-label form-label small mb-0">{{ $t('party_type_optional') }}</label>
            <FormDropdown
              v-model="party_type"
              :options="partyTypeOptions"
              clearable
              class="header-field"
            />
          </div>
          <div class="header-error"></div>
        </div>

        <!-- Space is kept while no party type is chosen, so picking one doesn't shift the form. -->
        <div class="col-md-6" :style="{ visibility: party_type ? 'visible' : 'hidden' }">
          <div class="d-flex align-items-center">
            <label class="header-label form-label small mb-0">
              {{ party_type ? $t(party_type) : '' }} <span class="text-danger">*</span>
            </label>
            <FormDropdown
              v-model="party_id"
              :options="partyOptions"
              :placeholder="$t(party_type === 'supplier' ? 'select_supplier' : 'select_customer')"
              :searchable="true"
              clearable
              class="header-field"
            />
          </div>
          <div class="header-error">{{ errors.party_id }}</div>
        </div>
      </div>

      <!-- Item Input Section -->
      <div class="row g-2 mb-3 align-items-start fs-7">
        <div class="col">
          <div class="position-relative">
            <!-- Search Input -->
            <input
            type="text"
            v-model="query"
            @input="debouncedSearch"
            :placeholder="$t('search_item_by_name_code')"
            class="form-control"
            @keydown="handleKeys"
          />

            <!-- Dropdown Search Results -->
            <div
              v-if="query"
              class="search-results shadow rounded bg-white position-absolute mt-2"
              style="width:450px; max-height: 450px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0;"
              :class="{ 'd-block': results.length > 0, 'd-none': results.length === 0 }"
            >

            <!-- Results List -->
            <ul class="list-group list-group-flush">
              <li
                v-for="(item, index) in results"
                :key="item.id"
                class="list-group-item d-flex justify-content-between align-items-center"
                :class="{ 'bg-primary text-white': index === itemIndex }"
                style="padding: 10px; font-size: 0.95rem; cursor: pointer;"
              >
                <!-- Clickable Product Info -->
                <div
                  class="flex-grow-1 d-flex flex-column"
                  @click="selectItem(item)"
                  style="cursor: pointer;"
                >
                  <div class="d-flex align-items-center gap-2">

                    <!-- Item Info -->
                    <div class="d-flex flex-column">
                      <strong class="text-dark">{{ item.node }} - {{ item.account_id }}</strong>
                    </div>
                  </div>
                </div>
              </li>
            </ul>

            <!-- No Results -->
            <div v-if="!results.length" class="text-center text-muted py-4" style="font-size: 1rem;">
              No results found
            </div>
          </div>
          </div>
          <div class="text-danger mt-1" style="height:0.50em;">
            <span v-if="errors.debit_item">{{ errors.item }}</span>
          </div>
        </div>

        <div class="col">
          <input type="text" class="form-control" :placeholder="$t('narration')" v-model="batches.narration" />
          <div class="text-danger mt-1" style="height:0.50em;">
            <span v-if="errors.narration">{{ errors.narration }}</span>
          </div>
        </div>

        <div class="col">
          <FormDropdown
              v-model="batches.type"
              :options="types"
              clearable
            />
          <div class="text-danger mt-1" style="height:0.50em;">
            <span v-if="errors.type">{{ errors.type }}</span>
          </div>
        </div>



        <div class="col">
          <input type="number" class="form-control" placeholder="amount" v-model="batches.amount" />
          <div class="text-danger mt-1" style="height:0.50em;">
            <span v-if="errors.debit_amount">{{ errors.amount }}</span>
          </div>
        </div>


        <div class="col-auto">
          <button type="button" class="btn btn-info" @click="addItem">{{$t('add')}}</button>
        </div>
      </div>

      <!-- Items Table -->
      <div class="table-responsive mb-3 fixed-table">
        <table class="table table-bordered table-striped">
          <thead class="table-light">
            <tr>
              <th>{{ $t("item") }}</th>
              <th>{{ $t("party") }}</th>
              <th>{{ $t("narration") }}</th>
              <th>{{ $t("type") }}</th>
              <th>{{ $t("credit") }}</th>
              <th>{{ $t("debit") }}</th>
              <th>{{ $t("action") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in addedItems" :key="index">
              <td>{{ item.item_name }}</td>
              <td>
                <template v-if="item.party_type">
                  <span class="badge bg-light text-dark me-1">{{ $t(item.party_type) }}</span>{{ item.party_name }}
                </template>
                <span v-else>—</span>
              </td>
              <td>{{ item.narration }}</td>
              <td>{{ item.type }}</td>
              <td>{{ item.type == 'credit' ? item.amount : 0 }}</td>
              <td>{{ item.type == 'debit' ? item.amount : 0 }}</td>
              <td>
                <a class="me-2 p-2" href="#" @click.prevent="editItem(item, index)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ti ti-edit">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </a>
                <button type="button" class="btn btn-sm btn-danger" @click="removeItem(index)">Delete</button>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="addedItems.length > 0">
            <tr>
              <th colspan="4" class="text-end">Total</th>
              <th>{{totalCreditAmount}}</th>
              <th>{{totalAmount}}</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- <div class="row g-2 mt-4 align-items-start">
        <div class="col"></div>
        <div class="col">
          <label class="form-label small fw-semibold">{{ $t('total_debit') }}</label>
          <input type="text" class="form-control text-end bg-light" :value="totalAmount" disabled />
        </div>
        <div class="col"></div>
        <div class="col">
          <label class="form-label small fw-semibold">{{ $t('total_credit') }}</label>
          <input type="text" class="form-control text-end bg-light" :value="totalCreditAmount" disabled />
        </div>
        <div class="col"></div>
        <div class="col"></div>
      </div> -->

      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="btn btn-cancel me-2" @click="cancelForm">{{ $t('cancel') }}</button>
        <button type="submit" class="btn btn-submit" :disabled="saving">{{ $t('submit') }}</button>
      </div>
    </form>
  </div>

  
</div>
</template>

<script>
import apiUrls from "@/urls";
import feather from "../../../../public/assets/js/feather.min";
import { useToast } from 'vue-toastification';
import debounce from 'lodash.debounce'
import { apiErrorMessage } from '@/helpers/apiError';
import { fetchVoucherHeads, searchVoucherHeads } from '@/helpers/voucherHeads';

export default {
  components: {
  },

  setup() {
    const toast = useToast()
    return {toast}
  },

  data() {
    return {
      batches: {
        item: null, 
        narration: '', 
        amount: '',
        voucher_no: '', 
        voucher_date: new Date().toISOString().split('T')[0],
        transaction_category:1,
        type:'',
        flag:'jr',

      },
      items: [],
      warehouses: [],
      suppliers: [],
      cash_types: [],
      errors: {},
      errorMessage: "",
      saving: false,

      query: "",

      results: [],
      itemIndex: 0,
      selectedItem: null,
      addedItems: [],
      bank_accounts: [],
      reference_no: '',
      cash_type: null,
      bank_account: null,

      debouncedSearch: null,
      headsRequest: null,
      transaction_category:[],
      types:[{id:'credit', text:'Credit'},{id:'debit',text:'Debit'}],
      party_type: '',
      party_id: null,
      restoringParty: false,
      customers: [],
    };
  },

  mounted() {
    this.fetchDropdownData();
    this.fetchTransactionCategory();
    this.fetchCustomers();
  },

  computed: {
    formattedItems() {
      return this.results.map(item => ({ id: item.id, text: item.node }));
    },

    formattedTransaction() {
      return this.transaction_category.map(transac => ({ id: transac.id, text: transac.category_name }));
    },

    formattedsuppliers() {
      return this.suppliers.map(supplier => ({
        id: supplier.id,
        text: supplier.phone_number ? `${supplier.name} (${supplier.phone_number})` : supplier.name,
      }));
    },

    formattedcustomers() {
      return this.customers.map(customer => ({
        id: customer.id,
        text: customer.customer_phone
          ? `${customer.customer_name} (${customer.customer_phone})`
          : customer.customer_name,
      }));
    },

    partyTypeOptions() {
      return [
        { id: 'customer', text: this.$t('customer') },
        { id: 'supplier', text: this.$t('supplier') },
      ];
    },

    partyOptions() {
      if (this.party_type === 'customer') return this.formattedcustomers;
      if (this.party_type === 'supplier') return this.formattedsuppliers;
      return [];
    },

    formattedcashtypes() {
      return this.cash_types.map(cash_type => ({ id: cash_type.id, text: cash_type.name }));
    },

    formattedbankaccounts() {
      return this.bank_accounts.map(bank_account => ({ id: bank_account.id, text: bank_account.name }));
    },

    isBank() {
      const val = this.cash_type;
      const id = val && typeof val === 'object' ? val.id : val;
      return String(id) === '1010102';
    },

    totalAmount() {
      return this.addedItems
        .filter(item => item.type === 'debit')
        .reduce((sum, item) => {
          return sum + (parseFloat(item.amount) || 0);
        }, 0)
        .toFixed(2);
    },

    totalCreditAmount() {
      return this.addedItems
        .filter(item => item.type === 'credit')
        .reduce((sum, item) => {
          return sum + (parseFloat(item.amount) || 0);
        }, 0)
        .toFixed(2);
    }
  },

  methods: {
    editItem(data, index){
      console.log(data)
      this.query = data.item_name
      this.batches.item = data.item
      this.batches.narration = data.narration
      this.batches.type = data.type
      this.batches.amount = data.amount
      this.selectedItem = {node: data.item_name, account_id:data.item_code}
      this.results = []
      this.itemIndex = 0;

      const party_type = data.party_type || '';
      if (party_type !== this.party_type) {
        this.restoringParty = true;
        this.party_type = party_type;
      }
      this.party_id = data.party_id || null;

      this.removeItem(index)

    },
    async fetchTransactionCategory() {
        try {
          let res = await this.$axios.get(apiUrls.transaction_category.get);
          this.transaction_category = res.data
          // No longer chosen on this form; the voucher still records the shop's default category.
          if (this.transaction_category.length) {
            this.batches.transaction_category = this.transaction_category[0].id;
          }
        } catch (err) {
          console.error(err);
        }
      },

      async fetchCustomers() {
        try {
          const res = await this.$axios.get(apiUrls.customers.get);
          // The API already leaves out the walk-in guest.
          this.customers = res?.data || [];
        } catch (err) {
          console.error('Error loading customers:', err);
        }
      },
      async fetchDropdownData() {
        try {
          const [
            itemRes, warehouseRes, supplierRes, cash_typeRes, receive_noRes, bank_accRes,
          ] = await Promise.all([
            this.$axios.get(apiUrls.item.get),
            this.$axios.get(apiUrls.inventory_warehouse.get),
            this.$axios.get(apiUrls.inventory_supplier.get),
            this.$axios.get(apiUrls.batch_cash_type.get),
            this.$axios.get(apiUrls.journal_voucher_number.get, { params: { voucher_date: this.batches.voucher_date } }),
            this.$axios.get(apiUrls.batch_bank_account.get)
          ]);

          this.items = itemRes?.data || [];
          this.warehouses = warehouseRes?.data || [];
          // "Opening Stock" is a placeholder supplier for stock entered with new items, not a real party.
          this.suppliers = (supplierRes?.data || []).filter(
            (supplier) => (supplier.name || '').trim().toLowerCase() !== 'opening stock'
          );
          this.cash_types = cash_typeRes?.data || [];
          this.batches.voucher_no = receive_noRes?.data?.voucher_no || '';
          this.bank_accounts = bank_accRes?.data || [];

          if (this.cash_types.length > 0 && !this.cash_type) {
            this.cash_type = this.cash_types[0].id;
          }

          if (this.bank_accounts.length > 0 && !this.bank_account) {
            this.bank_account = this.bank_accounts[0].id;
          }

        } catch (error) {
          console.error('Error loading dropdown data:', error);
        }
      },

      async searchItems() {
        if (!this.query) {
          this.results = []
          return
        }

        try {
          if (!this.headsRequest) this.headsRequest = fetchVoucherHeads(this.$axios);
          this.results = searchVoucherHeads(await this.headsRequest, this.query);
          this.itemIndex = 0;

          

          // render feather icons
          await this.$nextTick(() => {
            if (window.feather) feather.replace()
          })
        } catch (error) {
          console.error('Search error:', error)
          this.headsRequest = null
          this.results = []
        }
      },


      selectItem(item) {
        console.log('SelectItem:::',item)
        this.selectedItem = item
        this.query = item.node
        this.batches.item = item.account_id
        this.results = []
        this.itemIndex = 0;
      },

      handleKeys(event) {
        if (event.key === 'ArrowDown') {
          this.itemIndex = Math.min(this.itemIndex + 1, this.results.length - 1);
          event.preventDefault();
        } else if (event.key === 'ArrowUp') {
          this.itemIndex = Math.max(this.itemIndex - 1, 0);
          event.preventDefault();
        } else if (event.key === 'Enter') {
          const selectedProduct = this.results[this.itemIndex];
          if (selectedProduct) {
            this.selectItem(selectedProduct);
          }
          event.preventDefault();
        }
      },

      addItem() {
        this.errors = {};

        if (!this.selectedItem) {
          this.errors.item = this.$t('error.required');
        }


        [
          "amount",
          "voucher_date",
          'type',
        ].forEach(field => {
          if (!this.batches[field]) this.errors[field] = this.$t('error.required');
        });

        if (this.party_type && !this.party_id) {
          this.errors.party_id = this.$t('error.required');
        }

        if (Object.keys(this.errors).length > 0) {
          return;
        }

        const party = this.partyOptions.find((option) => option.id === this.party_id);
        const newItem = {
          ...this.batches,
          // id: this.selectedItem.id,
          item_name: this.selectedItem.node,
          item_code: this.selectedItem.account_id,
          party_type: this.party_type || null,
          party_id: this.party_type ? this.party_id : null,
          party_name: party ? party.text : '',
        };

        this.addedItems.push(newItem);
        this.clearRowFields();
      },



      async fetchReceivingNo() {
        try {
          let res = await this.$axios.get(apiUrls.journal_voucher_number.get, {
            params: { voucher_date: this.batches.voucher_date }
          });
          this.batches.voucher_no = res.data.voucher_no;
        } catch (err) {
          console.error(err);
        }
      },

      async submitBatch() {
        if (this.saving) return;
        if (!this.addedItems.length) {
          this.toast.warning("Please add at least one journal row before submitting.");
          return;
        }

        if (parseFloat(this.totalAmount) !== parseFloat(this.totalCreditAmount)) {
          this.toast.warning("Debit and credit totals must be equal.");
          return;
        }

        this.saving = true;
        try {
          const payload = {
            batches: this.addedItems.map(({ party_name, ...row }) => ({
              ...row,
              transaction_category: this.batches.transaction_category,
              voucher_no: this.batches.voucher_no,
              voucher_date: this.batches.voucher_date,
            })),
          };

          await this.$axios.post(apiUrls.journal_voucher_create.post, payload);

          this.toast.success("Journal voucher submitted successfully!");
          this.$router.push({ name: 'JournalVoucher' });
          this.$emit("create_success");
          this.resetForm();
          this.addedItems = [];
        } catch (error) {
          this.toast.error(apiErrorMessage(error));
          if (error.response?.data?.errors) {
            this.errors = error.response.data.errors;
          }
        } finally {
          this.saving = false;
        }
      },

      cancelForm() {
        this.$router.push({ name: 'JournalVoucher' });
      },

      removeItem(index) {
        this.addedItems.splice(index, 1);
      },

      clearRowFields() {
        const voucher_no = this.batches.voucher_no;
        const voucher_date = this.batches.voucher_date;

        this.batches = {
          item: null,
          narration: '',
          amount: '',
          type: '',
          voucher_no,
          voucher_date,
          transaction_category: this.batches.transaction_category,
          flag:'jr',
        };
        this.query = '';
        this.results = [];
        this.itemIndex = 0;
        this.selectedItem = null;
        this.errors = {};
      },

      normalizeSelectValue(value) {
        return value && typeof value === 'object' ? value.id : value;
      },

      resetForm() {
        this.batches = {
          item: null,
          narration: '',
          type: '',
          amount: '',
          voucher_no: '',
          voucher_date: new Date().toISOString().split('T')[0],
          flag:'jr',
        };
        this.query = '';
        this.debitResults = [];
        this.results = [];
        this.itemIndex = 0;
        this.selectedItem = null;
        this.errors = {};
        this.errorMessage = "";
        this.fetchReceivingNo();
      }
    },

  watch: {
    'batches.voucher_date'(newDate) {
      if (newDate) {
        this.fetchReceivingNo();
      } else {
        this.batches.voucher_no = '';
      }
    },

    'cash_type'(newVal) {
      const id = newVal && typeof newVal === 'object' ? newVal.id: newVal;
      if (String(id) === '1010102') {
        if (this.bank_accounts.length > 0) {
          this.bank_account = this.bank_accounts[0].id;
        }
      } else {
        this.bank_account = null;
      }
    },

    isBank(newVal) {
      if (!newVal) this.bank_account = null;
    },

    party_type() {
      // Editing a grid row puts its party back in the header; keep that party_id.
      if (this.restoringParty) {
        this.restoringParty = false;
        return;
      }
      this.party_id = null;
      if (this.errors.party_id) this.errors = { ...this.errors, party_id: undefined };
    },
  },

  created() {
    this.debouncedSearch = debounce(this.searchItems, 300)
  },
};
</script>


<style scoped>
.voucher-header .header-label {
  flex: 0 0 200px;
  margin-right: 1rem;
}

.voucher-header .header-field {
  flex: 1 1 0;
  min-width: 0;
}

/* Same height as the inputs beside them */
.voucher-header :deep(.form-basic-dropdown > .btn) {
  min-height: calc(1.6em + 1rem + 2px);
  font-size: 0.875rem;
}

.voucher-header .header-error {
  min-height: 1.25rem;
  margin-left: calc(200px + 1rem);
  font-size: 0.8rem;
  color: #dc3545;
}

/* Optional: adjust table and input heights */
.table td, .table th {
  vertical-align: middle;
}

.fixed-table {
  min-height: 350px;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 5px;
}

/* Modal Backdrop */
.confirm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* Modal Box */
.confirm-modal {
  width: 420px;
  background: #ffffff;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: fadeInScale 0.2s ease;
}

/* Header */
.confirm-header h4 {
  text-align: center;
  margin-bottom: 20px;
  font-weight: 600;
}

/* Body Rows */
.confirm-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.confirm-row.due {
  color: #dc3545;
  font-weight: 600;
  font-size: 16px;
}

/* Buttons */
.confirm-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

/* Animation */
@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

</style>

